/* eslint-disable jira/filename-case */

import { fg } from '@atlassian/jira-feature-gating';
import {
	// eslint-disable-next-line jira/restricted/@atlassian/react-resource-router
	createResource,
	type Match,
	type Query,
	type RouteResource,
	useResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import {
	fireErrorAnalytics,
	getStatusCodeGroup,
} from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { fetchProjectSidebarConnectNavigationInfo } from '../../../services/sidebar/project-sidebar-connect-addons-navigation/fetchProjectSidebarConnectNavigationInfo.tsx';
import type { ProjectSidebarAddonNavItem } from '../../../services/sidebar/project-sidebar-connect-addons-navigation/types.tsx';

const getIdentifier = (match: Match, query: Query): string => {
	const { projectKey, projectId } = getProjectKeyId(match, query);
	return projectKey || projectId;
};

export const staticProjectSettingsSidebarConnectAddonsNavigationResource: RouteResource<
	ProjectSidebarAddonNavItem[] | null
> = createResource<ProjectSidebarAddonNavItem[] | null>({
	type: 'NAVIGATION_SIDEBAR_PROJECT_SETTINGS_CONNECT_ADDONS_RESOURCE',
	// oxlint-disable-next-line no-explicit-any
	getKey: ({ match, query }: { match: any; query: any }) => getIdentifier(match, query) ?? 'none',
	getData: async ({ match, query, route }) => {
		const projectKeyOrId = getIdentifier(match, query);
		if (projectKeyOrId == null) {
			return null;
		}
		try {
			return await fetchProjectSidebarConnectNavigationInfo(projectKeyOrId, 'settings');
		} catch (error) {
			if (
				[
					'software-summary',
					'software-summary-embed',
					'software-summary-classic',
					'software-summary-classic-embed',
					'classic-business-summary',
					'classic-business-summary-embed',
				].includes(route.name) &&
				fg('jira_agentic_summary_m2_killswitch')
			) {
				fireErrorAnalytics({
					error: new Error('Unable to load Jira agent recommendation data'),
					meta: {
						id: 'fetchChatConnections',
						packageName: 'jiraContextualSummary',
						teamName: 'jlove-makkuro',
					},
					attributes: {
						statusCodeGroup: error instanceof Error ? getStatusCodeGroup(error) : 'unknown',
					},
					skipSentry: true,
				});
			}
			throw error;
		}
	},
	maxAge: 5 * 60 * 1000,
	isPageShellCritical: true,
});

export const useProjectSettingsSidebarConnectAddonsNavigationResource = (): UseResourceHookResponse<
	ProjectSidebarAddonNavItem[] | null
> => useResource(staticProjectSettingsSidebarConnectAddonsNavigationResource);
